/**
 * Admin Layout - Admin Dashboard Layout
 */
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users,
  Calendar,
  FileText,
  Settings,
  Plug,
  BarChart3,
  MapPin,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Shield,
  ArrowLeft
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

// OFW Logo Component
function OFWLogo() {
  return (
    <div className="flex items-center gap-3">
      <div className="bg-black px-3 py-1.5 flex items-center">
        <span className="text-xl font-heading font-bold tracking-tight">
          <span className="text-white">O</span>
          <span className="text-white">F</span>
          <span className="text-[#DB2777]">W</span>
        </span>
      </div>
      <span className="hidden sm:block text-xs font-heading font-semibold tracking-widest text-white">
        ADMIN
      </span>
    </div>
  );
}

export function AdminLayout({ children }) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { profile, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/admin' },
    { name: 'Members', icon: Users, path: '/admin/members' },
    { name: 'Classes', icon: Calendar, path: '/admin/classes' },
    { name: 'Locations', icon: MapPin, path: '/admin/locations' },
    { name: 'CMS Pages', icon: FileText, path: '/admin/cms/pages' },
    { name: 'Analytics', icon: BarChart3, path: '/admin/analytics' },
    { name: 'Integrations', icon: Plug, path: '/admin/integrations' },
    { name: 'Settings', icon: Settings, path: '/admin/settings' },
  ];

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-neutral-900">
      {/* Top Navigation */}
      <header className="fixed top-0 left-0 right-0 h-16 bg-neutral-900 border-b border-neutral-800 z-40">
        <div className="flex items-center justify-between h-full px-4 lg:px-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="lg:hidden p-2 text-neutral-400 hover:bg-neutral-800 rounded-lg"
            >
              {isSidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            <Link to="/">
              <OFWLogo />
            </Link>
          </div>

          <div className="relative">
            <button
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="flex items-center gap-3 p-2 hover:bg-neutral-800 rounded-lg"
            >
              <div className="w-8 h-8 rounded-full bg-[#3B82F6] flex items-center justify-center">
                <Shield className="w-4 h-4 text-white" />
              </div>
              <span className="hidden sm:block text-sm font-medium text-white">
                {profile?.full_name || 'Admin'}
              </span>
              <ChevronDown className="w-4 h-4 text-neutral-400" />
            </button>

            {isUserMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-48 bg-neutral-800 border border-neutral-700 rounded-lg py-2 shadow-lg">
                <Link
                  to="/admin/settings"
                  className="flex items-center gap-2 px-4 py-2 text-sm text-neutral-300 hover:bg-neutral-700"
                  onClick={() => setIsUserMenuOpen(false)}
                >
                  <Settings className="w-4 h-4" />
                  Settings
                </Link>
                <Link
                  to="/"
                  className="flex items-center gap-2 px-4 py-2 text-sm text-neutral-300 hover:bg-neutral-700"
                  onClick={() => setIsUserMenuOpen(false)}
                >
                  Back to Site
                </Link>
                <hr className="my-2 border-neutral-700" />
                <button
                  onClick={() => {
                    setIsUserMenuOpen(false);
                    handleLogout();
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-400 hover:bg-neutral-700"
                >
                  <LogOut className="w-4 h-4" />
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <aside className={`fixed top-16 left-0 bottom-0 w-64 bg-neutral-900 border-r border-neutral-800 z-30 transition-transform lg:translate-x-0 ${
        isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <nav className="p-4 space-y-1">
          {/* Back to Member Portal Link */}
          <Link
            to="/portal"
            onClick={() => setIsSidebarOpen(false)}
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-cyan-400 hover:bg-cyan-500/10 border border-cyan-500/30 mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Member Portal</span>
          </Link>
          
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive 
                    ? 'bg-[#3B82F6] text-white' 
                    : 'text-neutral-400 hover:bg-neutral-800 hover:text-white'
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.name}</span>
              </Link>
            );
          })}
        </nav>
      </aside>

      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-20 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:pl-64 pt-16 min-h-screen">
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}

export default AdminLayout;
